import { LOOP_SAMPLES, LOOP_SECONDS, TEMPO_PRESETS, TRACK_NAMES, VIDEO_SECONDS } from '../engine/constants'
import type { Recording, TempoPercent } from '../engine/constants'
import { MosaicRenderer } from '../engine/renderer'
import { Player } from '../engine/player'
import type { StatusKind } from '../engine/player'

const SPEAKER_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M17 9l4 6"/><path d="M21 9l-4 6"/></svg>'

function required<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector)
  if (!el) throw new Error(`Missing required element: ${selector}`)
  return el
}

function formatTime(seconds: number): string {
  const s = Math.max(0, seconds)
  const m = Math.floor(s / 60)
  return `${String(m).padStart(2, '0')}:${(s - m * 60).toFixed(3).padStart(6, '0')}`
}

export function mount(recordings: readonly Recording[], active: Recording): Player {
  const grid = required<HTMLCanvasElement>('#grid')
  const focus = required<HTMLCanvasElement>('#focus')
  const gridControls = required<HTMLDivElement>('#gridControls')
  const focusWrap = required<HTMLDivElement>('#focusWrap')
  const focusLabel = required<HTMLDivElement>('#focusLabel')

  const playBtn = required<HTMLButtonElement>('#play')
  const stopBtn = required<HTMLButtonElement>('#stop')
  const loopToggle = required<HTMLButtonElement>('#loopToggle')
  const timeline = required<HTMLInputElement>('#timeline')
  const timeEl = required<HTMLElement>('#time')
  const tempoButtons = required<HTMLDivElement>('#tempoButtons')
  const badge = required<HTMLElement>('#badge')
  const statusEl = required<HTMLElement>('#status')
  const syncEl = required<HTMLElement>('#sync')

  const loopStartEl = required<HTMLInputElement>('#loopStart')
  const loopEndEl = required<HTMLInputElement>('#loopEnd')
  const loopStartOut = required<HTMLOutputElement>('#loopStartOut')
  const loopEndOut = required<HTMLOutputElement>('#loopEndOut')

  // -- recording picker -------------------------------------------------------

  // Switching recordings is a full page reload with ?r=<id>. Every timing fact
  // in the engine derives from the active recording, and tearing down a live
  // AudioContext + WebCodecs pipeline to rebuild it in place buys nothing: the
  // new context needs a fresh user gesture anyway.
  const recordingRow = required<HTMLDivElement>('#recordingRow')
  const recordingSelect = required<HTMLSelectElement>('#recordingSelect')
  if (recordings.length > 1) {
    recordingRow.hidden = false
    for (const rec of recordings) {
      const option = document.createElement('option')
      option.value = rec.id
      option.textContent = rec.title
      option.selected = rec.id === active.id
      recordingSelect.appendChild(option)
    }
    recordingSelect.addEventListener('change', () => {
      const url = new URL(window.location.href)
      if (recordingSelect.value === recordings[0]!.id) url.searchParams.delete('r')
      else url.searchParams.set('r', recordingSelect.value)
      window.location.href = url.toString()
    })
  }

  // Ranges in the markup carry the built-in recording's numbers; rewrite them
  // for whichever recording is actually active.
  timeline.max = String(LOOP_SECONDS)
  loopStartEl.max = String(Math.max(0.001, VIDEO_SECONDS - 0.001))
  loopEndEl.max = String(VIDEO_SECONDS)
  loopEndEl.value = String(LOOP_SECONDS)
  loopEndOut.textContent = `${LOOP_SECONDS.toFixed(3)} s`

  const renderer = new MosaicRenderer(grid, focus)

  let scrubbing = false
  let scrubWasPlaying = false
  const muteButtons: HTMLButtonElement[] = []

  const player = new Player(renderer, {
    status(message: string, kind: StatusKind = 'info') {
      statusEl.textContent = message
      statusEl.className = `status${kind === 'info' ? '' : ` ${kind}`}`
    },
    time(musical: number) {
      if (!scrubbing) timeline.value = String(musical)
      timeEl.textContent = `${formatTime(musical)} / ${formatTime(player.state.loopEnabled ? player.state.loopEnd : VIDEO_SECONDS)}`
    },
    changed() {
      syncUi()
    },
  })

  // -- tiles -----------------------------------------------------------------

  TRACK_NAMES.forEach((name, i) => {
    const tile = document.createElement('div')
    tile.className = 'tile'

    const select = document.createElement('button')
    select.type = 'button'
    select.className = 'tile-select'
    select.setAttribute('aria-label', `Focus ${name}`)
    select.addEventListener('click', () => player.setSelected(i))

    const label = document.createElement('span')
    label.className = 'tile-name'
    label.textContent = name

    const mute = document.createElement('button')
    mute.type = 'button'
    mute.className = 'mute'
    mute.innerHTML = SPEAKER_ICON
    mute.setAttribute('aria-label', `Mute ${name}`)
    mute.setAttribute('aria-pressed', 'false')
    mute.addEventListener('click', (e) => {
      e.stopPropagation()
      player.toggleMute(i)
    })

    tile.append(select, label, mute)
    gridControls.appendChild(tile)
    muteButtons[i] = mute
  })

  // -- tempo -----------------------------------------------------------------

  const defaultTempo = TEMPO_PRESETS.includes(100) ? 100 : TEMPO_PRESETS[TEMPO_PRESETS.length - 1]!
  const tempoEls = new Map<TempoPercent, HTMLButtonElement>()
  for (const percent of TEMPO_PRESETS) {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = `${percent}%`
    button.setAttribute('aria-pressed', String(percent === defaultTempo))
    button.addEventListener('click', () => void player.setTempo(percent))
    tempoButtons.appendChild(button)
    tempoEls.set(percent, button)
  }
  if (defaultTempo !== 100) void player.setTempo(defaultTempo)

  // -- transport -------------------------------------------------------------

  playBtn.addEventListener('click', () => void player.togglePlay())
  stopBtn.addEventListener('click', () => player.stop())
  loopToggle.addEventListener('click', () => player.setLoopEnabled(!player.state.loopEnabled))

  timeline.addEventListener('pointerdown', () => {
    scrubbing = true
    scrubWasPlaying = player.state.playing
    if (scrubWasPlaying) player.pause()
  })

  const commitScrub = () => {
    if (!scrubbing) return
    scrubbing = false
    player.seek(Number(timeline.value))
    if (scrubWasPlaying) player.play()
  }

  timeline.addEventListener('input', () => {
    if (scrubbing) timeEl.textContent = `${formatTime(Number(timeline.value))} / ${formatTime(player.state.loopEnd)}`
  })
  timeline.addEventListener('pointerup', commitScrub)
  timeline.addEventListener('pointercancel', commitScrub)
  // Keyboard users never emit pointer events.
  timeline.addEventListener('change', () => {
    if (scrubbing) commitScrub()
    else player.seek(Number(timeline.value))
  })

  // -- loop points -----------------------------------------------------------

  const commitLoop = () => {
    player.setLoopRegion(Number(loopStartEl.value), Number(loopEndEl.value))
    const { loopStart, loopEnd } = player.state
    loopStartEl.value = String(loopStart)
    loopEndEl.value = String(loopEnd)
    loopStartOut.textContent = `${loopStart.toFixed(3)} s`
    loopEndOut.textContent = `${loopEnd.toFixed(3)} s`
    timeline.max = String(loopEnd)
  }

  loopStartEl.addEventListener('input', () => {
    loopStartOut.textContent = `${Number(loopStartEl.value).toFixed(3)} s`
  })
  loopEndEl.addEventListener('input', () => {
    loopEndOut.textContent = `${Number(loopEndEl.value).toFixed(3)} s`
  })
  loopStartEl.addEventListener('change', commitLoop)
  loopEndEl.addEventListener('change', commitLoop)

  required<HTMLButtonElement>('#setStart').addEventListener('click', () => {
    loopStartEl.value = String(Math.min(player.position, player.state.loopEnd - 0.01))
    commitLoop()
  })
  required<HTMLButtonElement>('#setEnd').addEventListener('click', () => {
    loopEndEl.value = String(Math.max(player.position, player.state.loopStart + 0.01))
    commitLoop()
  })
  required<HTMLButtonElement>('#musicalFull').addEventListener('click', () => {
    loopStartEl.value = '0'
    loopEndEl.value = String(LOOP_SECONDS)
    commitLoop()
  })
  required<HTMLButtonElement>('#mediaFull').addEventListener('click', () => {
    loopStartEl.value = '0'
    loopEndEl.value = String(VIDEO_SECONDS)
    commitLoop()
  })

  // -- keyboard --------------------------------------------------------------

  window.addEventListener('keydown', (e) => {
    if (e.code !== 'Space') return
    const tag = document.activeElement?.tagName ?? ''
    if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'SUMMARY') return
    e.preventDefault()
    void player.togglePlay()
  })

  // -- reflect state ---------------------------------------------------------

  function syncUi(): void {
    const s = player.state

    playBtn.textContent = s.playing ? 'Pause' : s.ready ? 'Play' : 'Prepare & play'
    playBtn.disabled = s.busy
    stopBtn.disabled = !s.ready
    loopToggle.textContent = `Loop: ${s.loopEnabled ? 'on' : 'off'}`
    loopToggle.setAttribute('aria-pressed', String(s.loopEnabled))

    focusLabel.textContent = TRACK_NAMES[s.selected] ?? ''
    focusWrap.classList.toggle('dim', s.muted[s.selected] === true)

    s.muted.forEach((isMuted, i) => {
      const button = muteButtons[i]
      if (!button) return
      button.setAttribute('aria-pressed', String(isMuted))
      button.setAttribute('aria-label', `${isMuted ? 'Unmute' : 'Mute'} ${TRACK_NAMES[i]}`)
    })

    for (const [percent, button] of tempoEls) {
      button.setAttribute('aria-pressed', String(percent === s.tempoPercent))
      button.disabled = s.busy
    }

    badge.textContent = s.ready
      ? `${s.playing ? 'Playing' : 'Ready'} · ${s.tempoPercent}% · loop sample ${LOOP_SAMPLES.toLocaleString('en-US')}`
      : 'Opening frame ready'

    syncEl.textContent = s.diagnostics
      ? `Sync: ${s.diagnostics.kind} — ${s.diagnostics.detail}`
      : ''
  }

  syncUi()
  return player
}
